import { Button, clx, Input, Label, Text } from "@medusajs/ui"
import { useState, type FormEvent } from "react"
import { useAuth } from "../lib/auth"
import { LANGUAGES, t, ts, useLocale } from "../lib/i18n"

export function LoginPage() {
  const { signIn } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    // Checked here rather than with `required`: Medusa's inputs paint any :invalid field red, so a
    // required field looked like an error before anyone had typed.
    if (!email.trim() || !password) {
      setError(t("Enter your email and password."))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await signIn(email.trim(), password)
    } catch (caught) {
      const status = (caught as { status?: number }).status
      const message = (caught as Error).message
      setError(status === 401 ? t("Wrong email or password.") : message ? ts(message) : t("Could not sign in."))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-y-4 px-4 py-12">
      <form onSubmit={submit} className="bg-ui-bg-base shadow-elevation-card-rest flex w-full max-w-sm flex-col gap-y-5 rounded-xl p-6">
        <div className="flex flex-col items-center gap-y-3">
          <div className="flex items-center gap-x-2.5">
            <img src="/logo.png" alt="High Club" className="h-10 w-auto" />
            <span className="hc-ref">HQ</span>
          </div>
          <h1 className="hc-title text-center text-[26px]">{t("Sign in")}</h1>
          <Text size="small" className="text-ui-fg-subtle text-center">
            {t("With your Medusa admin email and password.")}
          </Text>
        </div>
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="email">{t("Email")}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor="password">{t("Password")}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        {error && (
          <Text size="small" className="text-ui-fg-error" role="alert">
            {error}
          </Text>
        )}
        <Button type="submit" size="large" isLoading={busy} className="w-full">
          {t("Sign in")}
        </Button>
      </form>
      <LanguageSwitch />
    </div>
  )
}

/**
 * English / Ελληνικά under the sign-in form, so HQ can be read in Greek from its first screen. Once
 * signed in, the choice is kept on the account too (lib/language.ts) and is changed in Settings.
 */
function LanguageSwitch() {
  const { locale, setLocale, switching } = useLocale()
  return (
    <div className="flex items-center gap-x-1" role="radiogroup" aria-label={t("Language")}>
      {LANGUAGES.map((language, index) => (
        <span key={language.code} className="flex items-center gap-x-1">
          {index > 0 && <span aria-hidden className="text-ui-fg-muted">·</span>}
          <button
            type="button"
            role="radio"
            aria-checked={language.code === locale}
            lang={language.code}
            disabled={switching}
            onClick={() => void setLocale(language.code)}
            className={clx(
              "txt-compact-small rounded-md px-2 py-1.5",
              language.code === locale ? "text-ui-fg-base font-medium" : "text-ui-fg-muted hover:text-ui-fg-subtle"
            )}
          >
            {language.name}
          </button>
        </span>
      ))}
    </div>
  )
}
